/**
 * The i18n bootstrap and the lazy locale loader (CWS-199).
 *
 * Only ``en`` — the fallback language every locale renders against for
 * missing keys — is statically bundled and registered at module init. The
 * other four locale payloads load through :func:`ensureLanguageResources`:
 * the active locale during :func:`initializeLanguage` (which ``main.tsx``
 * awaits before the first render, so the render gate keeps its semantics),
 * the rest in the background after ``load`` (see
 * :func:`scheduleBackgroundLocalePreload`). Loading a locale means both
 * registering its i18next bundle **and** its FAQ payload — the FAQ resolver
 * and the head-adjacent FAQ/JSON-LD surface read the same file's
 * ``faqs``/``meta`` trees through ``faqLocalizedStore``'s registry, and a
 * language whose FAQ source is missing makes that resolver throw, so the
 * loader never lets a language become active half-registered.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import { registerFaqLocale, resetFaqRegistryForTests } from "@/lib/faqLocalizedStore";
import { normalizeSeoLanguage, SEO_LANGUAGES, type SeoLanguage } from "@/lib/seoMetadata";
import { resolveInitialLanguage } from "../lib/languageResolution";

/** Searchable prefix for every loader failure log (the logTrackFailure tradition). */
const LOG_PREFIX = "[locale-loader]";

/**
 * The dynamic loaders for the four dieted locale payloads.
 *
 * ``en`` has no entry: it is statically imported and registered at init
 * below, so :func:`ensureLanguageResources` resolves for it without a fetch.
 */
const LOCALE_LOADERS: Record<Exclude<SeoLanguage, "en">, () => Promise<{ default: unknown }>> = {
  de: () => import("./locales/de.json"),
  fr: () => import("./locales/fr.json"),
  it: () => import("./locales/it.json"),
  es: () => import("./locales/es.json"),
};

/**
 * In-flight loads, for single-flight semantics
 *
 * A language with a pending load resolves every concurrent ``ensure`` from
 * the one fetch. A rejected load removes its own entry, so a later ensure —
 * a re-clicked flag, the rescue effect's retry — re-attempts the fetch
 * rather than replaying the rejection. The browser's module map still caches
 * fetch failures per document (whatwg/html#6768, the state CWS-196 recorded),
 * so a re-attempt while the failure persists rejects quickly; recovery for a
 * persistently failed locale is the next navigation or reload.
 */
const pendingLoads = new Map<SeoLanguage, Promise<void>>();

/**
 * The active loader map
 *
 * Rebindable only through :func:`setLocaleLoadersForTests` (module mocking
 * cannot drive the rejection dynamics: Vitest caches a mock factory's
 * successful result per registry, so a locale that must fail **after**
 * having succeeded is unobservable through ``vi.mock``).
 */
let localeLoaders = LOCALE_LOADERS;

/**
 * Replace the dynamic loaders with test doubles
 *
 * Test-only (``src/i18n/lazyLocaleResources.test.tsx``), like
 * :func:`resetLocaleRegistrationForTests`; production never calls it.
 * Passing ``null`` restores the real loaders.
 *
 * Args:
 *     loaders: The replacement map, or ``null`` to restore.
 */
export function setLocaleLoadersForTests(
  loaders: typeof LOCALE_LOADERS | null,
): void {
  localeLoaders = loaders ?? LOCALE_LOADERS;
}

const i18nInitPromise = i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  lng: "en",
  fallbackLng: "en",
  // The literal array stays (the established declaration site the
  // localization contracts pin by source text); `SEO_LANGUAGES` above types
  // the loader map, so a missing locale is a type error there.
  supportedLngs: ["en", "de", "fr", "it", "es"],
  // The four dieted locales are not in `resources` and there is no backend:
  // without this flag init would treat every language as loaded and a
  // missing locale would surface as raw keys at render time instead of as a
  // failed ensure the switch paths refuse on.
  partialBundledLanguages: true,
  interpolation: {
    escapeValue: false,
  },
});

// The fallback floor is registered synchronously with its FAQ payload — the
// FAQ resolver reads `en` for a missing-key fallback in any active language,
// so it must never observe an unregistered `en`.
registerFaqLocale("en", en);

/**
 * True when a language's bundle is registered and safe to switch onto
 *
 * The bundle and the FAQ payload always register together (one loader, one
 * ``then``), so the bundle's presence is the whole condition.
 *
 * Args:
 *     lang: The locale to check.
 *
 * Returns:
 *     ``true`` when :func:`ensureLanguageResources` would resolve without a
 *     fetch.
 */
function isLocaleRegistered(lang: SeoLanguage): boolean {
  return lang === "en" || i18n.hasResourceBundle(lang, "translation");
}

/**
 * Ensure a language's payload is registered before the caller proceeds
 *
 * Resolves immediately for ``en`` and already-registered languages. For the
 * rest it dynamic-imports the payload, registers the i18next bundle and the
 * FAQ store slot together, and single-flights concurrent callers through one
 * fetch. A rejection is propagated to every waiter and evicted from the
 * cache, so a later call re-attempts.
 *
 * Args:
 *     lang: The language to make renderable. An unsupported string resolves
 *         as ``en`` (the fallback floor), matching the bootstrapping
 *         language-resolution semantics.
 *
 * Returns:
 *     Resolved when the language is fully registered.
 */
export function ensureLanguageResources(lang: string): Promise<void> {
  const safeLang = normalizeSeoLanguage(lang);
  if (isLocaleRegistered(safeLang)) {
    return Promise.resolve();
  }
  const pending = pendingLoads.get(safeLang);
  if (pending) {
    return pending;
  }
  const load = localeLoaders[safeLang]()
    .then((module) => {
      // The dynamic import resolves to the module namespace; the JSON payload
      // is its `default` export. Registering the namespace itself would file
      // every translation under a "default" key — every lookup misses, and
      // the page silently renders the English fallback.
      const payload = module.default;
      i18n.addResourceBundle(safeLang, "translation", payload, true, true);
      registerFaqLocale(safeLang, payload);
    })
    .catch((error: unknown) => {
      pendingLoads.delete(safeLang);
      throw error;
    });
  pendingLoads.set(safeLang, load);
  return load;
}

/** The suppression seam's state; see :func:`isBackgroundLocalePreloadInFlight`. */
let backgroundPreloadInFlight = false;

/**
 * Whether a background locale preload is in flight right now
 *
 * The ``vitePreloadErrorRecovery`` suppression seam (see ``main.tsx``'s
 * install call): Vite fires one identity-less ``vite:preloadError`` for every
 * failed wrapped dynamic import, and without this flag a benign background
 * preload failure — a class the preloader handles itself by logging and
 * skipping — escalates to a full-page reload that also cancels whatever lazy
 * navigation is in flight (the nightly's 2026-09-22 firefox failure). The
 * flag brackets each background load: the failure's event dispatch runs
 * inside the load's rejection path, before the ``finally`` clears the flag,
 * so the handler observes it. Bootstrap and switch-path ensures never raise
 * it, keeping their own failure semantics.
 *
 * Returns:
 *     ``true`` between ``preloadRemainingLocales`` starting a locale's load
 *     and that load settling (resolved or rejected).
 */
export function isBackgroundLocalePreloadInFlight(): boolean {
  return backgroundPreloadInFlight;
}

/**
 * Load every language except the active one, in the background
 *
 * Called from ``main.tsx`` after ``load``; sequential so the four fetches do
 * not burst onto the network at once. A per-locale failure is logged and
 * skipped — the queue continues, and a later switch to the failed language
 * pays its own ensure, whose refusal semantics are the switch path's to
 * apply. Each load raises :func:`isBackgroundLocalePreloadInFlight` for its
 * duration — the recovery-reload suppression window.
 *
 * Args:
 *     active: The language already registered by the bootstrap gate.
 *
 * Returns:
 *     Resolved when every remaining locale has registered or failed.
 */
export async function preloadRemainingLocales(active: string): Promise<void> {
  const activeLang = normalizeSeoLanguage(active);
  for (const lang of SEO_LANGUAGES) {
    if (lang === activeLang) {
      continue;
    }
    backgroundPreloadInFlight = true;
    try {
      await ensureLanguageResources(lang);
    } catch (error) {
      console.error(`${LOG_PREFIX} background preload failed for ${lang}`, error);
    } finally {
      backgroundPreloadInFlight = false;
    }
  }
}

let preloadScheduled = false;

/**
 * Schedule the background preloader off the load-critical path
 *
 * Waits for the ``window`` ``load`` event, then the first idle callback
 * (``requestIdleCallback`` with a ``setTimeout`` fallback), because on four
 * of the five Lighthouse-scanned families the LCP resource is the route
 * chunk — typically still in flight when ``render()`` returns — and the four
 * locale fetches must not compete with it. Because ``main.tsx``'s geo await
 * can outlast ``load`` on a fast connection, an already-fired ``load`` is
 * detected via ``document.readyState`` and the schedule starts immediately:
 * a listener attached after the event never runs its callback, which would
 * silently skip exactly the geo-resolved visitors. Idempotent; safe to call
 * once per page load, which is how ``main.tsx`` uses it.
 *
 * Args:
 *     active: The language already registered by the bootstrap gate.
 */
export function scheduleBackgroundLocalePreload(active: string): void {
  if (preloadScheduled) {
    return;
  }
  preloadScheduled = true;
  const startOnIdle = () => {
    const run = () => {
      void preloadRemainingLocales(active);
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => run(), { timeout: 3000 });
    } else {
      window.setTimeout(run, 50);
    }
  };
  if (document.readyState === "complete") {
    startOnIdle();
  } else {
    window.addEventListener("load", startOnIdle, { once: true });
  }
}

/**
 * Resolve the visitor's language and make it renderable
 *
 * The bootstrapping language resolution (URL first, then the persisted
 * choice, then the geo lookup), awaited by ``main.tsx`` before the first
 * render. The active locale is fully registered before
 * ``i18n.changeLanguage`` runs, so the first render never observes a
 * half-registered language. **Bootstrap-failure rule:** if the active
 * locale's load fails, this logs once and proceeds with ``en`` — at
 * bootstrap no render exists to protect and the HTML is fresh, so an English
 * first paint of the visitor's URL is strictly better than a blank page (the
 * recovery's stale-URL rationale does not apply). On a prefixed URL the
 * rescue effect is the designed retry path from there: it re-runs the ensure
 * after first paint and self-heals the page to the URL's language when the
 * network has recovered.
 *
 * **Bare-root pin (CWS-237):** on ``/`` the served document is the
 * prerendered English homepage, so the boot pins ``en`` — hydration must
 * match the served tree (the CWS-232 contract), and skipping the geo lookup
 * keeps the first paint off the network. The persisted choice and the geo
 * resolution still decide the visitor's experience: the root route
 * (``RootDocument``) re-resolves after hydration and redirects to
 * ``/{lang}``. Every other path resolves through the full chain unchanged.
 *
 * Returns:
 *     The language the render gate settled on — the resolved language, or
 *     ``en`` under the failure rule.
 */
export async function initializeLanguage(): Promise<SeoLanguage> {
  await i18nInitPromise;
  let language =
    window.location.pathname === "/" ? "en" : await resolveInitialLanguage();
  try {
    await ensureLanguageResources(language);
  } catch (error) {
    console.error(
      `${LOG_PREFIX} bootstrap load failed for ${language}; rendering the English fallback`,
      error,
    );
    language = "en";
  }
  await i18n.changeLanguage(language);
  return language;
}

/**
 * Forget every registered dieted locale and drop the in-flight loads
 *
 * Test-only (``src/i18n/lazyLocaleResources.test.tsx``): the global jsdom
 * seam in ``src/test/setup.ts`` registers all five languages through the
 * production loader before any test runs, so the loader's own dynamics —
 * idempotence, eviction-on-rejection, preloader isolation, the scheduled
 * preload — are observable only after a reset (which also clears the
 * schedule latch). Production never calls this; nothing in ``src/``
 * outside tests may.
 */
export function resetLocaleRegistrationForTests(): void {
  pendingLoads.clear();
  resetFaqRegistryForTests();
  preloadScheduled = false;
  for (const lang of SEO_LANGUAGES) {
    if (lang === "en") {
      continue;
    }
    if (i18n.hasResourceBundle(lang, "translation")) {
      i18n.removeResourceBundle(lang, "translation");
    }
  }
}

export default i18n;
